import React, { useState, useLayoutEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import { Router } from 'react-router-dom';
import { toastr } from 'utils';
import { ConfirmModal } from 'components/ConfirmModal/ConfirmModal';
import { UpgradeTierModal } from 'components';
import ImpersonationBanner from 'containers/SideNavigation/ImpersonationBanner';
import history from 'utils/history';
import { GlobalCustomProperties } from 'constants/globalCustomProperties';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './containers/App/app';

import i18n from './i18n';
import 'assets/scss/theme.scss';
import 'react-toastify/dist/ReactToastify.css';

const MOUNT_NODE = document.getElementById('root');

// Polyfill for React 18
// eslint-disable-next-line no-shadow
const HistoryRouter = ({ history, ...props }) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => history.listen(setState), [history]);

  return (
    <Router
      {...props}
      location={state.location}
      navigationType={state.action}
      navigator={history}
    />
  );
};

// This prevents from reloading page after every code change
if (module.hot && process.env.NODE_ENV !== 'production') {
  module.hot.accept();
}

const root = createRoot(MOUNT_NODE);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (error) => error?.status >= 500,
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      toastr.error(i18n.t('toastr:error'), error?.message || i18n.t('toastr:defaultError'));
    },
  }),
});

root.render(
  <QueryClientProvider client={queryClient}>
    <ConfirmModal />
    <UpgradeTierModal />
    <ToastContainer position="top-right" newestOnTop closeOnClick pauseOnHover theme="light" />
    <HistoryRouter history={history}>
      <ImpersonationBanner />
      <GlobalCustomProperties />
      <App />
    </HistoryRouter>
  </QueryClientProvider>,
);
